import React, { useEffect, useState, useCallback } from 'react';
import { MobileScreen, MobileHeader, MobileTabBar, CardSkeleton, ErrorState } from '../components/ui';
import { IconTruck, IconClock, IconCheck } from '../components/icons';
import { registerServiceWorker } from '../lib/pwa';
import { supabase } from '../lib/supabase';
import { initSync } from '../lib/portalQueue';
import { CrewSignInScreen, CrewSetupScreen } from '../components/crew/AuthScreens';
import JobsTab from '../components/crew/JobsTab';
import JobScreen from '../components/crew/JobScreen';
import RecapFlow from '../components/crew/RecapFlow';
import HoursTab from '../components/crew/HoursTab';
import EndOfDayTab from '../components/crew/EndOfDayTab';
import { getPortalAuthHeaders, clearPersistedToken } from '../components/crew/portalShared';

// Crew Lead Daily Portal — SPA route at /crew
//
// Reads the JSON payload from api/crew-portal-data.js and renders it on
// the phone shell (BBM Context for claude/mobile.md, Jaylen's Crew Portal
// Showroom picks 2026-09-15): bottom tabs Jobs · Hours · End of day, job
// cards that open a job screen, a five-step recap, office toasts.
//
// Auth is email + password only (Supabase JWT, crew-auth-plan-v1; the
// token+PIN flow was retired 2026-06-24). Writes go through the durable
// offline queue in lib/portalQueue.js.
//
// Screen map (state machine below):
//   loading → ready | error | auth (sign-in)   /crew/setup → CrewSetupScreen
//   ready:
//     tab 'jobs'  → JobsTab ─ tap a card → screen {type:'job', id}
//                                          └ Start recap → {type:'recap', id, step 1–5}
//     tab 'hours' → HoursTab (CrewHoursPanel, Save pinned)
//     tab 'eod'   → EndOfDayTab (checklist · truck photo · Sign out)

const TABS = [
    { key: 'jobs', label: 'Jobs', icon: <IconTruck size={22} /> },
    { key: 'hours', label: 'Hours', icon: <IconClock size={22} /> },
    { key: 'eod', label: 'End of day', icon: <IconCheck size={22} /> },
];

export default function CrewPortalPage() {
    const [state, setState] = useState({ status: 'loading', error: null, data: null, auth: null });
    const [reloadKey, setReloadKey] = useState(0);
    // Phone-shell navigation: which tab, and (on the Jobs tab) which screen
    // sits on top of it. `screen` is null (the list), { type: 'job', id }
    // or { type: 'recap', id, step }.
    const [tab, setTab] = useState('jobs');
    const [screen, setScreen] = useState(null);
    // Job whose recap just went into the offline queue (shows the amber
    // banner on its job screen even before the queue snapshot catches up).
    const [queuedRecapFor, setQueuedRecapFor] = useState(null);

    // Boot the offline write-queue: wire fresh JWT headers at sync time +
    // the reconnect/focus/visibility flush triggers. Idempotent.
    useEffect(() => { initSync(getPortalAuthHeaders); }, []);

    // /crew/setup — SMS invite landing (crew-auth-plan-v1). Standalone
    // screen, no data fetch; on success it redirects to /crew signed in.
    const isSetupRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/crew/setup');

    useEffect(() => {
        if (isSetupRoute) return undefined;
        let cancelled = false;
        (async () => {
            const authHeaders = await getPortalAuthHeaders();
            if (cancelled) return;
            if (!authHeaders.Authorization) {
                setState({ status: 'auth', error: null, data: null, auth: { mode: 'sign-in' } });
                return;
            }

            // Office preview passthrough (2026-08-06): the dispatch page
            // opens /crew?preview_crew=<id>&preview_date=<iso> in a new
            // tab. The office user's Supabase session (same origin) rides
            // the Authorization header; the API verifies it belongs to a
            // STAFF account before serving another crew member's payload.
            const previewQS = (() => {
                try {
                    const p = new URLSearchParams(window.location.search);
                    const c = p.get('preview_crew');
                    if (!c) return '';
                    const d = p.get('preview_date') || '';
                    return `?preview_crew=${encodeURIComponent(c)}${d ? `&preview_date=${encodeURIComponent(d)}` : ''}`;
                } catch { return ''; }
            })();

            try {
                const res = await fetch(`/api/crew-portal-data${previewQS}`, {
                    credentials: 'include',
                    // no-store (QA M5 NEW-1): without it the browser could
                    // heuristically serve a cached response on refresh.
                    cache: 'no-store',
                    headers: { 'Accept': 'application/json', ...authHeaders },
                });
                if (cancelled) return;

                if (res.status === 401) {
                    // Session expired/invalid — back to sign-in.
                    try { await supabase.auth.signOut(); } catch {}
                    setState({ status: 'auth', error: null, data: null, auth: { mode: 'sign-in' } });
                    return;
                }
                if (!res.ok) {
                    let msg = `Server returned ${res.status}`;
                    try { const body = await res.json(); if (body?.error) msg = body.error; } catch {}
                    setState({ status: 'error', error: msg, data: null, auth: null });
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                setState({ status: 'ready', error: null, data, auth: null, fetchedAt: Date.now() });
            } catch (err) {
                if (cancelled) return;
                setState({ status: 'error', error: err?.message || 'Network error', data: null, auth: null });
            }
        })();

        return () => { cancelled = true; };
    }, [reloadKey]);

    // PR 4a — register the service worker once the crew lead has
    // authenticated (never on an office PREVIEW load — the dispatcher's
    // browser shouldn't start receiving crew push).
    useEffect(() => {
        if (state.status === 'ready' && !state.data?.preview) {
            registerServiceWorker().catch(() => {});
        }
    }, [state.status]);

    // PR 4d — BBM_JOB_UPDATED messages from the service worker (posted on
    // every push event) re-run the data fetch so the lead doesn't have to
    // close-and-reopen the PWA to see the change.
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
        const onMessage = (event) => {
            const data = event && event.data;
            if (!data || data.type !== 'BBM_JOB_UPDATED') return;
            setReloadKey((k) => k + 1);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, []);

    // Tab title while mounted. The apple-mobile-web-app-title meta and the
    // manifest link are handled by an inline script in index.html — they
    // have to run BEFORE React mounts for Add-to-Home-Screen.
    useEffect(() => {
        const previousDocTitle = document.title;
        document.title = 'Crew Portal — Beaty Brothers Moving';
        return () => { document.title = previousDocTitle; };
    }, []);

    const handleAuthed = useCallback(() => {
        setState({ status: 'loading', error: null, data: null, auth: null });
        setReloadKey(k => k + 1);
    }, []);

    const refresh = useCallback(() => setReloadKey(k => k + 1), []);

    const signOut = useCallback(async () => {
        try { await supabase.auth.signOut(); } catch {}
        clearPersistedToken();
        window.location.replace('/crew');
    }, []);

    if (isSetupRoute) return <CrewSetupScreen />;

    if (state.status === 'loading') {
        return (
            <MobileScreen header={<MobileHeader title="Today" sub="Loading your day…" />}>
                <CardSkeleton count={2} className="sm:!grid-cols-1 lg:!grid-cols-1" />
            </MobileScreen>
        );
    }

    if (state.status === 'error') {
        return (
            <MobileScreen header={<MobileHeader title="Crew portal" />} bodyClassName="flex flex-col justify-center">
                <ErrorState title="Couldn’t load your day" hint={state.error || 'The server didn’t answer. Try again in a moment.'} onRetry={refresh} />
            </MobileScreen>
        );
    }

    if (state.status === 'auth') return <CrewSignInScreen onAuthed={handleAuthed} />;

    const { crewMember, validDate, jobs, clients, allCrew, crewDayHours, allTrucks, allPhotos, allWaivers, allEstimates, waiverTypes, materialTypes, materialsTaxRatePct, vapidPublicKey, tomorrowDate, tomorrowJobs, preview } = state.data;
    // Email/password only — no legacy portal token. Writes carry the JWT
    // header; push opt-in (token-keyed) stays hidden until re-keyed to crew_id.
    const portalToken = null;
    const isLeadOnAny = jobs.some(j => j.leader_id === crewMember.id);
    const ledJobs = jobs.filter(j => j.leader_id === crewMember.id);
    // Day-panel break ownership (QA M5 BUG-1): once the lead has saved a
    // Crew Hours row for today, the DAY PANEL owns the job's billing break
    // — the recap's lunch goes read-only and resubmits stop writing lunch.
    const leadDayRowExists = (crewDayHours || []).some(r => r.crew_member_id === crewMember.id);

    // Office preview banner (2026-08-06) — a dispatcher looking at the
    // lead's portal. Read-only by construction: the API's POST actions
    // reject staff JWTs. Rides in the shell's strip slot (not fixed).
    const strip = preview ? (
        <div className="flex-shrink-0 bg-amber-400 text-amber-950 text-center text-xs font-bold px-3 py-1.5">
            👁 PREVIEW — {preview.crew_name}'s portal for {new Date(preview.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · read-only
        </div>
    ) : null;

    const goTab = (key) => { setTab(key); setScreen(null); setQueuedRecapFor(null); };
    const tabs = <MobileTabBar items={TABS} value={tab} onChange={goTab} />;

    const screenJob = screen ? jobs.find(j => j.id === screen.id) : null;

    // Recap flow — its own full screen (no tab bar, so a stray tap can't
    // drop the draft). Keyed by job so the draft lives for the whole flow.
    if (screenJob && screen.type === 'recap') {
        const jobCrew = (screenJob.crew_ids || []).map(id => allCrew.find(c => c.id === id)).filter(Boolean);
        const client = clients.find(c => c.id === screenJob.client_id);
        return (
            <RecapFlow
                key={screenJob.id}
                job={screenJob}
                clientName={client?.name || 'Client'}
                jobCrew={jobCrew}
                materialTypes={materialTypes}
                materialsTaxRatePct={materialsTaxRatePct}
                dayHoursSaved={leadDayRowExists}
                waivers={(allWaivers || []).filter(w => w.client_id === screenJob.client_id)}
                waiverTypes={waiverTypes}
                token={portalToken}
                step={screen.step || 1}
                onStep={(n) => setScreen({ type: 'recap', id: screenJob.id, step: n })}
                onExit={() => setScreen({ type: 'job', id: screenJob.id })}
                onChanged={refresh}
                onSubmitted={({ queued }) => {
                    setQueuedRecapFor(queued ? screenJob.id : null);
                    setScreen({ type: 'job', id: screenJob.id });
                }}
                strip={strip}
            />
        );
    }

    if (screenJob && screen.type === 'job') {
        return (
            <JobScreen
                key={screenJob.id}
                job={screenJob}
                clients={clients}
                allCrew={allCrew}
                allTrucks={allTrucks}
                allPhotos={allPhotos}
                allWaivers={allWaivers}
                allEstimates={allEstimates}
                waiverTypes={waiverTypes}
                validDate={validDate}
                crewId={crewMember.id}
                token={portalToken}
                onChanged={refresh}
                onBack={() => { setScreen(null); setQueuedRecapFor(null); }}
                onStartRecap={() => setScreen({ type: 'recap', id: screenJob.id, step: 1 })}
                justQueued={queuedRecapFor === screenJob.id}
                tabs={tabs}
                strip={strip}
            />
        );
    }

    if (tab === 'hours') {
        return (
            <HoursTab
                key={`hours:${validDate}`}
                isLead={isLeadOnAny}
                ledJobs={ledJobs}
                allCrew={allCrew}
                clients={clients}
                validDate={validDate}
                crewDayHours={crewDayHours || []}
                token={portalToken}
                onSaved={refresh}
                tabs={tabs}
                strip={strip}
            />
        );
    }

    if (tab === 'eod') {
        return (
            <EndOfDayTab
                isLead={isLeadOnAny}
                ledJobs={ledJobs}
                crewDayHours={crewDayHours || []}
                validDate={validDate}
                token={portalToken}
                onSaved={refresh}
                onSignOut={signOut}
                tabs={tabs}
                strip={strip}
            />
        );
    }

    return (
        <JobsTab
            jobs={jobs}
            tomorrowJobs={tomorrowJobs}
            tomorrowDate={tomorrowDate}
            clients={clients}
            allCrew={allCrew}
            allTrucks={allTrucks}
            validDate={validDate}
            crewMember={crewMember}
            vapidPublicKey={vapidPublicKey}
            portalToken={portalToken}
            refresh={refresh}
            onOpenJob={(id) => setScreen({ type: 'job', id })}
            tabs={tabs}
            strip={strip}
        />
    );
}
